/**
 * Formatierung für ausgehende E-Mails.
 *
 * Der Text wird weiterhin als Klartext gespeichert — mit einer schlanken
 * Auszeichnung: `**fett**`, `*kursiv*`, `- Stichpunkt`. Erst beim Versand
 * entsteht daraus HTML.
 *
 * Warum nicht ein richtiger WYSIWYG-Editor, der direkt HTML speichert:
 *
 *   - Der gespeicherte Text bleibt lesbar. Kundenakte, Vorschauzeile, Suche
 *     und Volltext arbeiten unverändert weiter — sie alle gehen von Klartext
 *     aus, und HTML in der Datenbank würde jede dieser Stellen brechen.
 *   - `document.execCommand` ist abgekündigt und liefert je nach Browser
 *     unterschiedliches, oft kaputtes HTML. Das müsste vor dem Versand wieder
 *     gesäubert werden — ein eigenes Projekt.
 *   - Eine Editor-Bibliothek wäre die schwerste Abhängigkeit im ganzen
 *     Projekt, für eine Handvoll Auszeichnungen.
 *
 * Die Empfänger bekommen beides: den Klartext und die HTML-Fassung. Wer einen
 * Klartext-Client benutzt, sieht `- Stichpunkt` statt einer Liste — lesbar
 * bleibt es allemal.
 *
 * Dieses Modul hat bewusst **keine Importe** und wird über
 * `@epasu/core/richtext` auch von der Web-App genutzt. Jeder Import hier würde
 * über die Kette mit in den Browser-Bundle wandern — `node:crypto`,
 * `better-sqlite3`, Dateisystem — und den Build zum Abbruch bringen.
 */

/* -------------------------------------------------------------------------- */
/*  Maskierung                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Macht Sonderzeichen unschädlich.
 *
 * Läuft **vor** jeder Auszeichnung. Tippt jemand `<b>` oder `<script>` in eine
 * Vorlage, soll das als Text ankommen und nicht als Markup — sonst entscheidet
 * der Inhalt der Vorlage darüber, was im Postfach des Kunden passiert.
 */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/* -------------------------------------------------------------------------- */
/*  Auszeichnung innerhalb einer Zeile                                        */
/* -------------------------------------------------------------------------- */

/**
 * Setzt fett, kursiv und Links innerhalb einer Zeile.
 *
 * Fett wird vor kursiv verarbeitet, sonst würde `**fett**` als zwei
 * ineinander verschachtelte Kursivstellen gelesen.
 */
function inlineMarkup(zeile: string): string {
  let out = zeile;

  // **fett**
  out = out.replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '<strong>$1</strong>');
  // *kursiv* und _kursiv_ — nicht mitten im Wort, damit datei_name_x heil bleibt
  out = out.replace(/(^|[\s(])\*(?=\S)([^*\n]*?\S)\*(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');
  out = out.replace(/(^|[\s(])_(?=\S)([^_\n]*?\S)_(?=[\s.,;:!?)]|$)/g, '$1<em>$2</em>');

  // [Text](url)
  out = out.replace(
    /\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/g,
    '<a href="$2">$1</a>',
  );
  // Nackte Adressen — nur, wenn sie nicht schon in einem Link stecken.
  out = out.replace(
    /(^|[\s(])(https?:\/\/[^\s<)]+)/g,
    (_ganz, davor: string, url: string) => `${davor}<a href="${url}">${url}</a>`,
  );

  return out;
}

/* -------------------------------------------------------------------------- */
/*  Ganzer Text                                                               */
/* -------------------------------------------------------------------------- */

const AUFZAEHLUNG = /^\s*[-*•]\s+(.*)$/;
const NUMMERIERT = /^\s*\d+[.)]\s+(.*)$/;

/**
 * Wandelt den ausgezeichneten Klartext in HTML für den Mailversand.
 *
 * Aufbau bewusst schlicht: Absätze, Zeilenumbrüche, zwei Listenarten, fett,
 * kursiv, Links. Keine Überschriften, keine Tabellen, keine Bilder — was eine
 * Geschäftsmail braucht, ist damit abgedeckt, und jede weitere Regel wäre eine
 * weitere Stelle, an der die Ausgabe unerwartet aussieht.
 */
export function markdownToHtml(text: string): string {
  const zeilen = escapeHtml(text.replace(/\r\n/g, '\n')).split('\n');
  const teile: string[] = [];

  let absatz: string[] = [];
  let liste: string[] | null = null;
  let listeNummeriert = false;

  const absatzSchliessen = (): void => {
    if (absatz.length === 0) return;
    teile.push(`<p>${absatz.map(inlineMarkup).join('<br>')}</p>`);
    absatz = [];
  };

  const listeSchliessen = (): void => {
    if (!liste) return;
    const tag = listeNummeriert ? 'ol' : 'ul';
    const punkte = liste.map((p) => `<li>${inlineMarkup(p)}</li>`).join('');
    teile.push(`<${tag}>${punkte}</${tag}>`);
    liste = null;
  };

  for (const zeile of zeilen) {
    const aufzaehlung = AUFZAEHLUNG.exec(zeile);
    const nummeriert = NUMMERIERT.exec(zeile);

    if (aufzaehlung || nummeriert) {
      absatzSchliessen();
      const nummer = Boolean(nummeriert);
      // Wechselt die Listenart, beginnt eine neue Liste.
      if (liste && listeNummeriert !== nummer) listeSchliessen();
      if (!liste) {
        liste = [];
        listeNummeriert = nummer;
      }
      liste.push((aufzaehlung?.[1] ?? nummeriert?.[1] ?? '').trim());
      continue;
    }

    if (zeile.trim() === '') {
      absatzSchliessen();
      listeSchliessen();
      continue;
    }

    listeSchliessen();
    absatz.push(zeile);
  }

  absatzSchliessen();
  listeSchliessen();

  return teile.join('\n');
}

/**
 * Enthält der Text überhaupt eine Auszeichnung?
 *
 * Ist nichts formatiert, geht die Mail als reiner Klartext raus. Eine
 * HTML-Fassung, die nur `<p>`-Absätze um denselben Text legt, bringt niemandem
 * etwas und lässt eine schlichte Nachricht bei manchen Spamfiltern schlechter
 * aussehen.
 */
export function hasFormatting(text: string): boolean {
  return text
    .split('\n')
    .some(
      (zeile) =>
        AUFZAEHLUNG.test(zeile) ||
        NUMMERIERT.test(zeile) ||
        /\*\*\S[\s\S]*?\S\*\*/.test(zeile) ||
        /(^|[\s(])[*_]\S[^*_\n]*?\S[*_](?=[\s.,;:!?)]|$)/.test(zeile) ||
        /\[[^\]\n]+\]\(https?:\/\/[^)\s]+\)/.test(zeile),
    );
}

/**
 * Entfernt die Auszeichnung — für Vorschauzeile und Suche.
 *
 * `**Angebot**` soll in der Liste als `Angebot` erscheinen, nicht mit
 * Sternchen.
 */
export function stripMarkup(text: string): string {
  return text
    .replace(/\*\*(?=\S)([\s\S]*?\S)\*\*/g, '$1')
    .replace(/(^|[\s(])[*_](?=\S)([^*_\n]*?\S)[*_](?=[\s.,;:!?)]|$)/g, '$1$2')
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^)\s]+)\)/g, '$1')
    .replace(/^\s*[-*•]\s+/gm, '• ')
    .replace(/^\s*(\d+)[.)]\s+/gm, '$1. ');
}
